import React, { useEffect, useState, useRef } from "react";
import { Calendar, MapPin, ArrowRight, Music } from "lucide-react";
import { Link } from "react-router-dom";

export default function UpcomingEvents({ events = [], loading = false }) {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting || entry.boundingClientRect.top < window.innerHeight) {
          setIsVisible(true);
          observer.unobserve(section);
        }
      },
      {
        threshold: [0, 0.1, 0.2],
        rootMargin: "50px"
      },
    );

    observer.observe(section);

    // Immediate check
    const rect = section.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      setIsVisible(true);
    }

    return () => {
      if (section) observer.unobserve(section);
      observer.disconnect();
    };
  }, []);

  const SkeletonEvent = () => (
    <div className="border-2 border-black/10 p-4 sm:p-6 lg:p-8 animate-pulse shadow-sm">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <div className="md:col-span-2 space-y-2">
          <div className="h-12 bg-gray-200 w-full rounded" />
          <div className="h-3 bg-gray-200 w-3/4 rounded mx-auto" />
        </div>
        <div className="md:col-span-7 space-y-4">
          <div className="h-8 bg-gray-200 w-3/4 rounded" />
          <div className="space-y-2">
            <div className="h-3 bg-gray-200 w-1/2 rounded" />
            <div className="h-3 bg-gray-200 w-1/3 rounded" />
          </div>
        </div>
        <div className="md:col-span-3 flex md:flex-col items-center md:items-end justify-center gap-4">
          <div className="h-6 bg-gray-200 w-20 rounded" />
          <div className="h-10 bg-gray-200 w-32 rounded" />
        </div>
      </div>
    </div>
  );

  return (
    <section
      ref={sectionRef}
      id="events"
      className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 bg-white overflow-hidden"
      style={{
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <div className="max-w-[1200px] mx-auto">
        {/* Section header */}
        <div
          className={`mb-12 sm:mb-16 lg:mb-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <h2
            className="text-3xl sm:text-4xl lg:text-6xl font-normal text-black mb-3 sm:mb-4"
            style={{
              fontFamily: "Playfair Display, Georgia, serif",
              fontWeight: 300,
              letterSpacing: "-0.01em",
            }}
          >
            Upcoming Performances
          </h2>
          <p
            className="text-base sm:text-lg text-black font-medium max-w-[600px]"
            style={{
              fontFamily: "Crimson Pro, Georgia, serif",
              fontStyle: "italic",
            }}
          >
            Experience the magic live. Join us for an unforgettable evening of
            timeless jazz.
          </p>
        </div>

        {/* Events list */}
        <div className="min-h-[400px]">
          {loading ? (
            <div className="space-y-4 sm:space-y-6">
              {[1, 2, 3, 4].map((i) => (
                <SkeletonEvent key={i} />
              ))}
            </div>
          ) : events.length > 0 ? (
            <div className="space-y-4 sm:space-y-6">
              {events.map((event, index) => (
                <article
                  key={event.id || index}
                  className={`group border-2 border-black/10 hover:border-black transition-all duration-500 hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] sm:hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transform hover:-translate-x-1 hover:-translate-y-1 ${isVisible
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-12"
                    }`}
                  style={{
                    transitionDelay: isVisible ? `${index * 100}ms` : "0ms",
                  }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6 p-4 sm:p-6 lg:p-8">
                    {/* Date block */}
                    <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-3 sm:gap-4 md:gap-0">
                      <div className="text-center flex-shrink-0">
                        <div
                          className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black leading-none"
                          style={{ fontFamily: "Playfair Display, Georgia, serif" }}
                        >
                          {event.day}
                        </div>
                        <div className="text-xs sm:text-sm font-bold text-black mt-1 tracking-widest uppercase">
                          {event.month}
                        </div>
                        <div className="text-xs text-black/90 font-medium mt-0.5 sm:mt-1">
                          {event.year}
                        </div>
                      </div>
                    </div>

                    {/* Event details */}
                    <div className="md:col-span-7 flex flex-col justify-center space-y-2 sm:space-y-3">
                      <h3
                        className="text-xl sm:text-2xl lg:text-3xl font-medium text-black"
                        style={{
                          fontFamily: "Playfair Display, Georgia, serif",
                        }}
                      >
                        {event.title}
                      </h3>

                      <div className="flex flex-col gap-2 text-xs sm:text-sm text-black font-medium">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="flex-shrink-0" />
                          <span>{event.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="flex-shrink-0" />
                          <span>{event.time}</span>
                        </div>
                      </div>

                      <p className="text-xs sm:text-sm text-black">
                        {event.venue}
                      </p>
                    </div>

                    {/* Status and CTA */}
                    <div className="md:col-span-3 flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-3 sm:gap-4">
                      <div
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold tracking-wider flex-shrink-0 uppercase ${event.event_status === "Sold Out"
                          ? "bg-black text-white"
                          : event.event_status === "Presale"
                            ? "bg-black/10 text-black"
                            : "bg-white border-2 border-black text-black"
                          }`}
                      >
                        {event.event_status}
                      </div>

                      {event.event_status === "On Sale" && event.ticket_url && (
                        <a
                          href={event.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group/btn bg-black text-white px-4 sm:px-6 py-2.5 sm:py-3 hover:bg-white hover:text-black border-2 border-black transition-all duration-300 flex items-center gap-2 touch-manipulation text-xs sm:text-sm whitespace-nowrap"
                          style={{
                            fontWeight: 600,
                            letterSpacing: "0.5px",
                          }}
                        >
                          GET TICKETS
                          <ArrowRight
                            size={14}
                            className="transition-transform duration-300 group-hover/btn:translate-x-1"
                          />
                        </a>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="py-20 text-center border-2 border-dashed border-black/10 bg-gray-50 rounded-xl">
              <Music size={48} className="mx-auto text-black/60 mb-4" />
              <p className="text-black font-serif italic text-sm uppercase tracking-widest">No upcoming performances at the moment.</p>
            </div>
          )}
        </div>

        {/* View all events */}
        <div
          className={`text-center mt-12 transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
        >
          <Link
            to="/events"
            className="inline-flex items-center gap-2 text-black hover:text-jazz-brown transition-colors duration-200 border-b-2 border-black hover:border-jazz-brown pb-1 touch-manipulation"
            style={{
              fontSize: "14px",
              fontWeight: 600,
              letterSpacing: "0.5px",
            }}
          >
            VIEW ALL UPCOMING EVENTS
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
